import { CommonModule } from '@angular/common';
import { Component, inject, Injector, input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { pairwise, startWith, withLatestFrom } from 'rxjs';
import {
    Item,
    SelectFieldComponent,
} from '../select-field/select-field.component';
import { RegioniProvinceComuniService } from './http/regioni-province-comuni.service';
import { RegioniProvinceComuniStore } from './store/regioni-province-comuni.store';

@Component({
   
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectFieldComponent,
    TranslatePipe,
  ],
  selector: 'siat-regioni-province-comuni',
  templateUrl: './regioni-province-comuni.component.html',
  providers: [RegioniProvinceComuniStore, RegioniProvinceComuniService],
})
@UntilDestroy()
export class RegioniProvinceComuniComponent implements OnInit {
  /**
   * The injections.
   */
  regioniProvinceComuniService = inject(RegioniProvinceComuniService);
  regioniProvinceComuniStore = inject(RegioniProvinceComuniStore);

  /**
   * The formgroup
   */
  parentFormgroup = input<FormGroup>();

  /**
   * The form controls
   */
  regioniControlName = input<string>();
  provinciaControlName = input<string>();
  comuneControlName = input<string>();

  /**
   * An array of classes to be added to the element.
   */
  containerClass = input<string>();

  /**
   * A flag to indicate if the fields are read-only.
   */
  areReadOnly = input<boolean>(false);

  private injector = inject(Injector);

  /**
   * Used to generate unique ID.
   * @private
   */
  private static nextId = 0;
  id = `siat-rpc-${RegioniProvinceComuniComponent.nextId++}-`;

  /**
   * O@inheritdoc
   */
  ngOnInit(): void {
    this.loadGetAllRegioni();
    this.subscribeToRegioniValueChanges();
    this.subscribeToProvinciaValueChanges();

    if (!this.regioneControl.value) {
      this.clearProvinciaControls();
      this.clearComuneControls();
    }
  }

  /**
   * Subscribes to the regioni control value changes.
   */
  subscribeToRegioniValueChanges(): void {
    const areReadOnly$ = toObservable(this.areReadOnly, {
      injector: this.injector,
    });

    this.regioneControl.valueChanges
      .pipe(
        startWith(this.regioneControl.value),
        pairwise(),
        withLatestFrom(areReadOnly$),
        untilDestroyed(this)
      )
      .subscribe(([[prevValue, currValue], isReadOnly]) => {
        if (!currValue) {
          this.clearProvinciaControls();
          this.clearComuneControls();
          return;
        }
        if (typeof currValue !== 'object') return;
        if (
          typeof prevValue === 'object' &&
          prevValue &&
          prevValue.id === currValue.id
        ) {
          return;
        }
        if (typeof prevValue === 'object' && prevValue) {
          this.provinciaControl.setValue(null, { emitEvent: false });
          this.comuneControl.setValue(null, { emitEvent: false });
        }
        if (!isReadOnly && this.regioneControl.valid) {
          this.provinciaControl.enable({ emitEvent: false });
        }
        this.loadGetAllProvince(currValue);
      });
  }

  /**
   * Subscribes to the provincia control value changes.
   */
  subscribeToProvinciaValueChanges(): void {
    const areReadOnly$ = toObservable(this.areReadOnly, {
      injector: this.injector,
    });

    this.provinciaControl.valueChanges
      .pipe(
        startWith(this.provinciaControl.value),
        pairwise(),
        withLatestFrom(areReadOnly$),
        untilDestroyed(this)
      )
      .subscribe(([[prevValue, currValue], isReadOnly]) => {
        if (!currValue) {
          this.clearComuneControls();
          return;
        }

        if (typeof currValue !== 'object') return;

        if (
          prevValue &&
          typeof prevValue === 'object' &&
          prevValue.id === currValue.id
        ) {
          return;
        }

        if (prevValue && typeof prevValue === 'object') {
          this.comuneControl.setValue(null, { emitEvent: false });
        }

        if (!isReadOnly && this.provinciaControl.valid) {
          this.comuneControl.enable({ emitEvent: false });
        }

        this.loadGetAllComuni(currValue);
      });
  }
  /**
   * Load all regioni.
   */
  private loadGetAllRegioni(): void {
    this.regioniProvinceComuniStore.fetchGetAllRegioni();
  }

  /**
   * Load all province.
   */
  private loadGetAllProvince(regione: Item): void {
    this.regioniProvinceComuniStore.fetchGetAllProvince(regione.name);
  }

  /**
   * Load all comuni.
   */
  private loadGetAllComuni(provincia: Item): void {
    this.regioniProvinceComuniStore.fetchGetAllComuni(provincia.name);
  }

  /**
   * Clear provincia control.
   */
  private clearProvinciaControls(): void {
    this.provinciaControl.reset(undefined, { emitEvent: false });
    this.provinciaControl.disable({ emitEvent: false });
  }

  /**
   * Clear comune control.
   */
  private clearComuneControls(): void {
    this.comuneControl.reset(undefined, { emitEvent: false });
    this.comuneControl.disable({ emitEvent: false });
  }

  /**
   * @returns {FormControl<Item>} of regione.
   */
  private get regioneControl(): FormControl<Item> {
    return this.parentFormgroup().get(
      this.regioniControlName()
    ) as FormControl<Item>;
  }

  /**
   * @returns {FormControl<Item>} of provincia.
   */
  private get provinciaControl(): FormControl<Item> {
    return this.parentFormgroup().get(
      this.provinciaControlName()
    ) as FormControl<Item>;
  }

  /**
   * @returns {FormControl<Item>} of comune.
   */
  private get comuneControl(): FormControl<Item> {
    return this.parentFormgroup().get(
      this.comuneControlName()
    ) as FormControl<Item>;
  }
}
